import { forwardRef, ReactNode, RefObject } from "react";

export type Props = {
  /** Total number of pages/items */
  totalPages: number;
  /** Current page index (0-based) */
  currentPageIndex: number;
  /** Reference to the scroll container for automatic scrolling */
  scrollContainerRef?: RefObject<HTMLDivElement>;
  /** Callback when a dot is clicked */
  onPageChange?: (pageIndex: number) => void;
  /** Additional CSS classes for the container */
  className?: string;
  /** Additional CSS classes for each dot */
  dotClassName?: string;
  /** Additional CSS classes for the active dot */
  activeDotClassName?: string;
  /** Custom dot renderer */
  renderDot?: (props: {
    index: number;
    isActive: boolean;
    onClick: () => void;
  }) => ReactNode;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base pagination dots component with no styling.
 * Displays clickable dots for navigation between pages/items.
 * Use render props or className props to customize appearance.
 *
 * @example
 * // In shared/ui/PaginationDots.tsx
 * import { PaginationDotsBase, type Props as PaginationDotsBaseProps } from "@core/ui";
 *
 * export function PaginationDots(props: PaginationDotsBaseProps) {
 *   return (
 *     <PaginationDotsBase
 *       {...props}
 *       className="flex justify-center items-center gap-2"
 *       dotClassName="w-2 h-2 rounded-full bg-brand-400"
 *       activeDotClassName="bg-brand-50 scale-125"
 *     />
 *   );
 * }
 */
export const PaginationDotsBase = forwardRef<HTMLDivElement, Props>(
  (
    {
      totalPages,
      currentPageIndex,
      scrollContainerRef,
      onPageChange,
      className,
      dotClassName,
      activeDotClassName,
      renderDot,
      "data-testid": testId = "pagination-dots",
    },
    ref
  ) => {
    const handleDotClick = (index: number) => {
      // Call the onPageChange callback if provided
      onPageChange?.(index);

      // Scroll the container if ref is provided
      if (scrollContainerRef?.current) {
        const targetScrollLeft = index * scrollContainerRef.current.clientWidth;
        scrollContainerRef.current.scrollTo({
          left: targetScrollLeft,
          behavior: "smooth",
        });
      }
    };

    return (
      <div ref={ref} className={className} data-testid={testId} role="tablist">
        {Array.from({ length: totalPages }).map((_, index) => {
          const isActive = index === currentPageIndex;

          if (renderDot) {
            return (
              <span key={index}>
                {renderDot({
                  index,
                  isActive,
                  onClick: () => handleDotClick(index),
                })}
              </span>
            );
          }

          return (
            <button
              key={index}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={`Go to page ${index + 1}`}
              className={`${dotClassName || ""} ${isActive ? activeDotClassName || "" : ""}`.trim() || undefined}
              onClick={() => handleDotClick(index)}
              data-testid={`${testId}-dot-${index}`}
            />
          );
        })}
      </div>
    );
  }
);

PaginationDotsBase.displayName = "PaginationDotsBase";

